import "./dashboard.scss";
import { Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { SectionHeader } from "@/sprout/typography";
import { usePage } from "@inertiajs/react";

import ProfileCard from "@components/profile_card";
import JobAlerts from "@/components/dashboard/widgets/job_alerts/widget";
import Tasks from "@/components/dashboard/widgets/tasks_widget";
import Applications from "@/components/dashboard/widgets/applications_widget";
import SearchSection from "@/components/dashboard/search_section.tsx";
import DashboardUploadResumeModal from "@components/dashboard/dashboard_upload_resume_modal";
import { type RecentSearch } from "@/types/job_search/recent_searches";

import useDevice from "@/hooks/use_device";
import useTranslation from "@/hooks/use_translation";
import useProfile from "@/queries/use_profile";
import Fingerprint15 from "../images/fingerprint_15.svg";
import { SurveyCallout } from "@components/survey/survey_callout";
import DreamJobBanner, { type DreamJobBannerState } from "@components/dashboard/dream_job_banner";
import { useEffectOnce } from "react-use";
import JobSearchWidget from "@components/dashboard/widgets/job_search_widget.tsx";
import JobRecommendationsSection from "@components/dashboard/job_recommendations_section.tsx";
import IdentityVerificationBanner from "@components/dashboard/identity_verification_banner";

export type Subscription = {
  boardName: string;
  filters: string[];
  path: string;
  editPath: string;
  logoUrl: string;
  updatedAt: Date;
};

type PageProps = {
  uploadResumeModal?: boolean;
  jobSeekersTasksEnabled?: boolean;
};

type Props = {
  firstName?: string;
  paths: {
    notificationSchedulePath: string;
  };
  closeOnLoad?: boolean;
  newUser: boolean;
  landedDreamJob?: boolean;
  recentSearches: RecentSearch[];
  dreamJobBanner?: { state: DreamJobBannerState; count: number };
  jobRecommendationsEnabled: boolean;
  newJobAlertsEnabled: boolean;
};

const MobileDashboard = ({
  recentSearches,
  jobRecommendationsEnabled,
  newJobAlertsEnabled,
}: Props) => {
  const { jobSeekersTasksEnabled } = usePage<PageProps>().props;

  return (
    <>
      <JobSearchWidget recentSearches={recentSearches} />
      <ProfileCard />
      {jobRecommendationsEnabled && <JobRecommendationsSection />}
      <Applications />
      {!jobSeekersTasksEnabled && <Tasks />}
      {!newJobAlertsEnabled && <JobAlerts />}
    </>
  );
};

const DesktopDashboard = ({
  recentSearches,
  jobRecommendationsEnabled,
  newJobAlertsEnabled,
}: Props) => {
  const { jobSeekersTasksEnabled } = usePage<PageProps>().props;
  return (
    <>
      <div className="dashboard__col1">
        <JobSearchWidget recentSearches={recentSearches} />
        {jobRecommendationsEnabled && <JobRecommendationsSection />}
        <Applications />
        {!jobSeekersTasksEnabled && <Tasks />}
      </div>
      <div className="dashboard__col2">
        <ProfileCard />
        {!newJobAlertsEnabled && <JobAlerts />}
      </div>
    </>
  );
};

const Dashboard = (props: Props) => {
  const { firstName, landedDreamJob, newUser, closeOnLoad, recentSearches, dreamJobBanner } = props;

  const { t } = useTranslation("dashboard");

  const device = useDevice();
  const { uploadResumeModal, jobSeekersTasksEnabled } = usePage<PageProps>().props;
  const { data: profile } = useProfile();
  const [showUploadResumeModal, setShowUploadResumeModal] = useState(false);

  useEffect(() => {
    if (!uploadResumeModal || profile?.resume || localStorage.getItem("uploadResumeModalShown")) {
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem("uploadResumeModalShown", "true");
      setShowUploadResumeModal(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [uploadResumeModal, profile?.resume]);

  useEffectOnce(() => {
    if (closeOnLoad) {
      window.close();
    }
  });

  const dashboardTitle = () => {
    if (newUser) {
      return t("header.title_new_user");
    }

    return firstName ? t("header.title_with_name", { name: firstName }) : t("header.title");
  };

  const dashboardSubtitle = () => {
    if (landedDreamJob) {
      return t("header.subtitle_dream_job");
    }

    if (newUser) {
      return t("header.subtitle_new_user");
    }

    const subtitles = t("header.subtitle", { returnObjects: true }) as string[];
    return subtitles[Math.floor(Math.random() * subtitles.length)];
  };

  const content = (
    <>
      {dreamJobBanner ? (
        <DreamJobBanner state={dreamJobBanner.state} count={dreamJobBanner.count} />
      ) : (
        <div className="welcome-banner with-search">
          <div className="welcome-container">
            <div className="welcome-text-wrapper">
              <div className="welcome-text">
                <SectionHeader serif large as="h1" className="welcome-title">
                  {dashboardTitle()}
                </SectionHeader>
                <SectionHeader className="welcome-subtitle">{dashboardSubtitle()}</SectionHeader>
              </div>
            </div>
            <SearchSection recentSearches={recentSearches} />
            <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
          </div>
          <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
        </div>
      )}
      <div className="dashboard">
        {jobSeekersTasksEnabled && (
          <ErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <IdentityVerificationBanner />
            </Suspense>
          </ErrorBoundary>
        )}
        <SurveyCallout />
        <main>
          {device.isMobile ? <MobileDashboard {...props} /> : <DesktopDashboard {...props} />}
        </main>
      </div>
      <DashboardUploadResumeModal
        open={showUploadResumeModal}
        onOpenChange={setShowUploadResumeModal}
      />
    </>
  );

  return content;
};

export default Dashboard;
